import axios from 'axios';

import { getAccessToken } from '@/apiRequest';
import { RV_ADDRESS_API_BASE_URL } from '@/config';
import addressCacheService, { normalizeAddress, getUserCountry } from '@/services/AddressCacheService';
import {
  getGeocodableEntityId,
  getKeyPickupAddress,
  getKeyReturnAddress,
  hasGeocodableAddress,
} from '@/services/geocodableAddressUtils';

export {
  getAccessInformationAddress,
  getAccessInformationValue,
  getGeocodableEntityId,
  getKeyPickupAddress,
  getKeyReturnAddress,
  getKeysDevolutionAddress,
  getOrderAccessInformation,
} from '@/services/geocodableAddressUtils';

export const GEOCODE_BATCH_SIZE = 10;

const toAddressPayload = (id, address) => ({
  id: String(id),
  street: address?.street ?? '',
  number: String(address?.number ?? ''),
  neighborhood: address?.neighborhood ?? '',
  city: address?.city ?? '',
  state: address?.state ?? '',
  zipcode: address?.zipcode ?? '',
  country: getUserCountry(),
});

const getOrder = item => item?.order ?? item;

const getInspectorAddress = inspector =>
  [
    inspector?.address,
    inspector?.details?.address,
    inspector?.residentialAddress,
    inspector?.residential_address,
    inspector?.homeAddress,
    inspector?.home_address,
    inspector?.details?.residentialAddress,
    inspector?.details?.residential_address,
    inspector?.details?.homeAddress,
    inspector?.details?.home_address,
    inspector?.details,
    inspector,
  ].find(hasGeocodableAddress);

const isOrderItem = item =>
  Boolean(
    item?.order ||
      item?.order_type_id ||
      item?.building_type ||
      item?.client ||
      item?.identifier ||
      item?.code ||
      item?.access_information ||
      item?.accessInformation ||
      item?.property_access ||
      item?.propertyAccess
  );

const addAddress = (addressesById, id, address) => {
  if (!id || !hasGeocodableAddress(address)) return;

  addressesById.set(String(id), toAddressPayload(id, address));
};

const addKeysAddresses = (addressesById, orderId, order) => {
  const pickupAddress = getKeyPickupAddress(order);
  const devolutionAddress = getKeyReturnAddress(order);

  if (pickupAddress) {
    addAddress(addressesById, `${orderId}:keys-pickup`, pickupAddress);
  }
  if (devolutionAddress) {
    addAddress(addressesById, `${orderId}:keys-return`, devolutionAddress);
  }
};

export const extractGeocodableAddresses = items => {
  const addressesById = new Map();

  (items || []).forEach(item => {
    if (!item) return;

    const order = getOrder(item);
    const orderId = getGeocodableEntityId(order);
    const inspectorAddress = getInspectorAddress(item);
    const isInspector = !isOrderItem(item) && Boolean(inspectorAddress);

    if (isInspector) {
      const inspectorId = getGeocodableEntityId(item);

      if (inspectorId) addAddress(addressesById, `${inspectorId}:base`, inspectorAddress);

      return;
    }

    addAddress(addressesById, orderId, order);

    if (orderId) addKeysAddresses(addressesById, orderId, order);
  });

  return [...addressesById.values()];
};

const chunkAddresses = addresses => {
  const chunks = [];

  for (let index = 0; index < addresses.length; index += GEOCODE_BATCH_SIZE) {
    chunks.push(addresses.slice(index, index + GEOCODE_BATCH_SIZE));
  }

  return chunks;
};

const groupAddressesByLocation = addresses => {
  const groupsByLocation = new Map();

  addresses.forEach(address => {
    const locationKey = normalizeAddress(address);
    const group = groupsByLocation.get(locationKey);

    if (group) {
      group.ids.push(address.id);
      return;
    }

    groupsByLocation.set(locationKey, {
      address,
      ids: [address.id],
    });
  });

  return [...groupsByLocation.values()];
};

const toCoordinates = result => ({
  latitude: result?.latitude ?? null,
  longitude: result?.longitude ?? null,
});

const hasCoordinatesResult = result =>
  result &&
  Object.prototype.hasOwnProperty.call(result, 'latitude') &&
  Object.prototype.hasOwnProperty.call(result, 'longitude');

export class GeocodeService {
  #client;
  #cache;
  #getToken;

  constructor({
    client = axios.create({ baseURL: RV_ADDRESS_API_BASE_URL }),
    cache = addressCacheService,
    getToken = getAccessToken,
  } = {}) {
    this.#client = client;
    this.#cache = cache;
    this.#getToken = getToken;
  }

  #getRequestConfig() {
    return {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.#getToken() || ''}`,
      },
    };
  }

  async #getCachedCoordinates(addresses) {
    try {
      return await this.#cache.getMany(addresses);
    } catch (error) {
      console.debug('GeocodeService cache read error', error);

      return addresses.map(() => null);
    }
  }

  async #cacheResults(results, addressesById) {
    const entries = results
      .filter(result => addressesById.has(String(result?.id)) && hasCoordinatesResult(result))
      .map(result => ({
        address: addressesById.get(String(result.id)),
        coordinates: result,
      }));

    if (!entries.length) return;

    try {
      await this.#cache.setMany(entries);
    } catch (error) {
      console.debug('GeocodeService cache write error', error);
    }
  }

  async fetchCoordinates(address) {
    const payload = toAddressPayload(getGeocodableEntityId(address) || 'address', address);

    try {
      const cachedCoordinates = await this.#cache.get(payload);

      if (cachedCoordinates) return toCoordinates(cachedCoordinates);
    } catch (error) {
      console.debug('GeocodeService cache read error', error);
    }

    try {
      const { id, ...requestPayload } = payload;
      const { data } = await this.#client.post('/addresses/geocode', requestPayload, this.#getRequestConfig());
      const result = data?.result ?? data;

      if (!hasCoordinatesResult(result)) return { latitude: null, longitude: null };

      try {
        await this.#cache.set(payload, result);
      } catch (error) {
        console.debug('GeocodeService cache write error', error);
      }

      return toCoordinates(result);
    } catch (error) {
      console.debug('GeocodeService.fetchCoordinates error', error);

      return { latitude: null, longitude: null };
    }
  }

  async fetchCoordinatesBatch(items) {
    const addresses = extractGeocodableAddresses(items);
    const coordinatesById = Object.fromEntries(
      addresses.map(address => [address.id, { latitude: null, longitude: null }])
    );

    if (!addresses.length) return coordinatesById;

    const addressGroups = groupAddressesByLocation(addresses);
    const uniqueAddresses = addressGroups.map(group => group.address);
    const cachedCoordinates = await this.#getCachedCoordinates(uniqueAddresses);
    const missingGroups = addressGroups.filter((group, index) => {
      const cachedResult = cachedCoordinates[index];

      if (!cachedResult) return true;

      group.ids.forEach(id => {
        coordinatesById[id] = toCoordinates(cachedResult);
      });

      return false;
    });

    const missingAddresses = missingGroups.map(group => group.address);
    const addressesById = new Map(missingAddresses.map(address => [address.id, address]));
    const addressGroupsById = new Map(missingGroups.map(group => [group.address.id, group]));
    const chunks = chunkAddresses(missingAddresses);

    for (const chunk of chunks) {
      try {
        const { data } = await this.#client.post(
          '/addresses/geocode-batch',
          { addresses: chunk },
          this.#getRequestConfig()
        );
        const results = Array.isArray(data?.results) ? data.results : [];

        results.forEach(result => {
          const id = String(result?.id);
          const addressGroup = addressGroupsById.get(id);

          if (addressGroup && hasCoordinatesResult(result)) {
            addressGroup.ids.forEach(addressId => {
              coordinatesById[addressId] = toCoordinates(result);
            });
          }
        });

        await this.#cacheResults(results, addressesById);
      } catch (error) {
        console.debug('GeocodeService.fetchCoordinatesBatch error', error);
      }
    }

    return coordinatesById;
  }
}

const geocodeService = new GeocodeService();

export const fetchCoordinates = address => geocodeService.fetchCoordinates(address);
export const fetchCoordinatesBatch = items => geocodeService.fetchCoordinatesBatch(items);

export default geocodeService;
